import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';
import { I as ICON_NAMES, a as ICONS } from './names.js';

// src/module/util/error/index.ts
var STRIP = "AbortError: ";
var AbortError = class extends Error {
  name = "AbortError";
  constructor(signal) {
    let reason;
    if (signal instanceof AbortSignal) {
      reason = String(signal.reason);
      reason = reason.startsWith(STRIP) ? reason.slice(STRIP.length) : reason;
    }
    super(reason);
  }
};

// src/module/util/promise/index.ts
var PromiseUtil = {
  /**
   * Returns a cached promise while it is pending
   *
   * Once the promise is resolved, the cache is cleared and subsequent calls will create a new promise.
   */
  coalesce(fn) {
    let promise;
    return (...args) => {
      if (promise) {
        return promise;
      }
      promise = fn(...args).finally(() => {
        promise = void 0;
      });
      return promise;
    };
  },
  /**
   * Returns a cached promise, ensuring the promise runs only once
   *
   * Unlike memoization, this returns the promise, not the result.
   */
  once(fn) {
    let promise = void 0;
    return (...args) => {
      if (promise) {
        return promise;
      }
      promise = fn(...args);
      return promise;
    };
  },
  /**
   * Extract a Promise's resolve and reject functions so that they can be fired externally.
   */
  extract() {
    let resolve;
    let reject;
    const promise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    return {
      promise,
      resolve: (res) => resolve(res),
      reject: (err) => reject(err)
    };
  },
  /**
   * Only return latest result.
   *
   * All promises that are created earlier but resolved later will throw an AbortError.
   */
  latest(fn) {
    let abortControllers = [];
    return async (...args) => {
      abortControllers.forEach((ctrl2) => {
        ctrl2.abort();
      });
      const ctrl = new AbortController();
      abortControllers.push(ctrl);
      try {
        const ret = await fn(...args);
        if (ctrl.signal.aborted) {
          throw new AbortError(ctrl.signal);
        }
        return ret;
      } finally {
        abortControllers = abortControllers.filter((c) => c !== ctrl);
      }
    };
  }
};

const iconCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;display:inline-flex;flex:none;font-family:Open Sans;font-size:.875rem;font-weight:400;height:100%;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;line-height:0!important;width:100%}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}div{display:block}svg{height:100%;width:100%}svg,svg *{pointer-events:none}:host([size=xs]){height:.5rem;width:.5rem}:host([size=xs][role=button]):before{content:\"\";inset:-10px;position:absolute}:host([size=sm]){height:.75rem;width:.75rem}:host([size=sm][role=button]):before{content:\"\";inset:-8px;position:absolute}:host([size=md]){height:1rem;width:1rem}:host([size=md][role=button]):before{content:\"\";inset:-4px;position:absolute}:host([size=lg]){height:1.25rem;width:1.25rem}:host([size=xl]){height:1.5rem;width:1.5rem}:host([size=\"2xl\"]){height:3rem;width:3rem}:host([role=button]){cursor:pointer;position:relative}:host([role=button])[disabled]{cursor:not-allowed}:host([role=button]):focus{outline:none}.rotate-90{rotate:90deg}.rotate-180{rotate:180deg}.rotate-270{rotate:270deg}";
const WuiIconStyle0 = iconCss;

function isIconName(name) {
    return Boolean(name !== 'NULL' && typeof name !== 'undefined' && name in ICONS);
}
const WuiIcon = /*@__PURE__*/ proxyCustomElement(class WuiIcon extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.getIconSvg = PromiseUtil.latest(async (name) => {
            if (!isIconName(name)) {
                return {
                    svg: '',
                    classes: [],
                };
            }
            const [importSvg, classes] = ICONS[name];
            const svg = (await importSvg()).default;
            return {
                svg,
                classes,
            };
        });
        this.name = 'NULL';
        this.size = 'md';
        this.classes = [];
        this.svg = '';
    }
    // We lazy load all icon SVGs by watching when the `name` property changes.
    // We then request the icon asynchronously, and once it's loaded, we replace
    // the internal state with the new SVG markup.
    //
    // We use abort controllers to make sure that we only handle the latest SVG request.
    // Any requests made earlier but that took longer to respond, will be ignored.
    async watchName(value) {
        try {
            const { svg, classes } = await this.getIconSvg(value);
            this.svg = svg;
            this.classes = classes;
        }
        catch (err) {
            console.error('icon', err);
            // ignore (abort) errors
        }
    }
    componentWillLoad() {
        // Load the initial icon SVG by triggering the watch function.
        this.watchName(this.name);
    }
    render() {
        if (!isIconName(this.name)) {
            return h(Host, null);
        }
        return (h(Host, null, h("div", { class: this.classes.join(' '), innerHTML: this.svg })));
    }
    static get watchers() { return {
        "name": ["watchName"]
    }; }
    static get style() { return WuiIconStyle0; }
}, [1, "wui-icon", {
        "name": [513],
        "size": [513],
        "classes": [32],
        "svg": [32]
    }, undefined, {
        "name": ["watchName"]
    }]);
WuiIcon.ICON_NAMES = ICON_NAMES;
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-icon"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-icon":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiIcon);
            }
            break;
    } });
}
defineCustomElement();

export { WuiIcon as W, defineCustomElement as d };

//# sourceMappingURL=icon.js.map